import { PluginType } from '../../components/plugins/types';
import { GrispiEvents } from '../../EventAndActionNames';
import {
    AgentStatus,
    agentStatusIdType,
    callClientStatusToCallPluginStatusMap,
    callPluginStatusToAgentStatusIdMap,
    callPluginStatusToCallClientStatusMap
} from '../../model/types';
import { PluginStore } from '../../store/childStore/pluginStore';
import { getPluginStore } from './getStore';

// const baseAgentStatusIds = [1, 6, 7, 8]; // 1: Offline, 6: Back Office, 7: Break, 8: Lunch Break
const callPluginsSpecificAgentStatusIds = [2, 3, 4, 5]; // 2: All Calls, 3: Inbound Calls, 4: Outbound Calls, 5: Call Logging,
const chatPluginsSpecificAgentStatusIds = [9]; // 9: Chat
const bothCallChatPluginsSpecificAgentStatusIds = [10, 11, 12, 13]; //  10: All Calls + Chat, 11: Inbound Calls + Chat, 12: Outbound Calls + Chat, 13: Call Logging + Chat

export const afterCallAgentStatusId = callPluginStatusToAgentStatusIdMap[callClientStatusToCallPluginStatusMap.resting]; // 5: Call Logging
export const afterCallAgentStatusWithChatId = 13;

export const isCallPluginRelatedAgentStatusId = (agentStatusId: agentStatusIdType): boolean =>
    callPluginsSpecificAgentStatusIds.includes(agentStatusId) ||
    bothCallChatPluginsSpecificAgentStatusIds.includes(agentStatusId);
export const isChatPluginRelatedAgentStatusId = (agentStatusId: agentStatusIdType): boolean =>
    chatPluginsSpecificAgentStatusIds.includes(agentStatusId) ||
    bothCallChatPluginsSpecificAgentStatusIds.includes(agentStatusId);

export const getPluginDefinitions = (): PluginStore['pluginDefinitions'] => getPluginStore().pluginDefinitions ?? {};
export const getChatPlugin = (): PluginStore['chatPlugin'] =>
    getPluginStore().chatPlugin ?? ({} as PluginStore['chatPlugin']);
export const getCallPlugin = (): PluginStore['callPlugin'] =>
    getPluginStore().callPlugin ?? ({} as PluginStore['callPlugin']);
export const getTranslatePlugin = (): PluginStore['translatePlugin'] =>
    getPluginStore().translatePlugin ?? ({} as PluginStore['translatePlugin']);

export const getAgentCurrentPluginStatus = <T extends keyof Pick<AgentStatus, 'call' | 'chat'>>(
    pluginType: T
): AgentStatus[T] => getPluginStore()?.currentAgentStatus?.[pluginType];

export const getAgentNewPluginStatus = <T extends keyof Pick<AgentStatus, 'call' | 'chat'>>(
    pluginType: T
): AgentStatus[T] => getPluginStore()?.newAgentStatus?.[pluginType];

export const getCallPluginsErrors = (): PluginStore['pluginsErrors']['CALL'] => getPluginStore().pluginsErrors.CALL;
export const getChatPluginErrors = (): PluginStore['pluginsErrors']['CHAT'] => getPluginStore().pluginsErrors.CHAT;

const isInitializingError = (error: string) => error === 'initializingError';
export const hasPluginTypeAnyErrors = <T extends PluginType>(
    pluginType: T,
    pluginTypeErrors?: PluginStore['pluginsErrors'][PluginType]
): boolean => {
    const errors = pluginTypeErrors || getPluginStore().pluginsErrors[pluginType];
    return Object.values(errors).some(isInitializingError);
};

export const isAnyPluginInitializing = <T extends PluginType>(
    pluginType?: T,
    pluginTypeInitializing?: PluginStore['pluginIsInitializing'][PluginType]
): boolean => {
    if (!pluginType) {
        return getPluginStore().isAnyPluginInitializing;
    }

    const pluginInitializingStatus = pluginTypeInitializing || getPluginStore().pluginIsInitializing[pluginType];
    return Object.values(pluginInitializingStatus).some((i) => i);
};
export const isAgentLoggedIntoCallPlugin = (pluginId: string): boolean =>
    getPluginStore().pluginIsInitializing.CALL?.[pluginId] === false;

export const isAnyPluginAvailable = (
    pluginDefinitions?: PluginStore['pluginDefinitions'],
    pluginType?: keyof typeof PluginType
): boolean => {
    const pluginDefinitionsToUse = pluginDefinitions || getPluginDefinitions();

    if (!pluginType) {
        return Object.keys(pluginDefinitionsToUse).length > 0;
    }

    return Object.values(pluginDefinitionsToUse).some((pd) => pd.pluginType === pluginType);
};

export const sendAgentStatusChangeEventToPlugins = (selectedAgentStatus: AgentStatus): boolean => {
    let shouldChangeAgentStatusWithApi = true;

    if (!selectedAgentStatus) {
        // console.debug('sendAgentStatusChangeEventToPlugins: selectedAgentStatus is undefined');

        shouldChangeAgentStatusWithApi = false;
        return shouldChangeAgentStatusWithApi;
    }

    // if (selectedAgentStatus.id === getPluginStore().currentAgentStatus?.id) {
    //     console.error(
    //         'sendAgentStatusChangeEventToPlugins: currentAgentStatusId is already set to this value',
    //         selectedAgentStatus.id,
    //     );

    //     shouldChangeAgentStatusWithApi = false;
    //     return shouldChangeAgentStatusWithApi;
    // }

    Object.entries(getPluginDefinitions()).forEach(([pluginId, definition]) => {
        if (definition.pluginType === PluginType.CHAT) {
            // no need to do anything extra for chat plugin
            return;
        }

        if (definition.pluginType === PluginType.CALL) {
            const newCallPluginStatus = selectedAgentStatus.call;

            if (getCallPluginsErrors()?.[pluginId]) {
                // prevent plugin interaction after plugin is in error state
                // console.debug(`[${pluginId}] plugin is in error state, not sending agent status change event`);
                return;
            }
            if (!isAgentLoggedIntoCallPlugin(pluginId)) {
                // prevent plugin interaction if plugin is in initialization state, i.e, agent is not logged into alotech
                // console.debug(`[${pluginId}] plugin is not logged in, not sending agent status change event`);
                return;
            }

            const statusInfo = callPluginStatusToCallClientStatusMap[newCallPluginStatus];
            const event = new CustomEvent(GrispiEvents.MESSAGE_TO_PLUGIN, {
                detail: {
                    pluginId,
                    data: { type: 'grispi.call.fn.setStatus', parameters: [statusInfo, { isExplicit: true }] }
                }
            });

            // console.debug('sendAgentStatusChangeEventToPlugins, sending event to plugin', event);
            // dispatch 'grispi.main.plugin.sendMessage' event in order to trigger change plugins status via grispi.call.fn.setStatus
            window.dispatchEvent(event);
            // PluginApp component will send status update info for the plugin (through CallApp) back to Grispi to finalize the status change (agentStatus will be updated) process via PluginStore

            // console.debug(
            //     'sendAgentStatusChangeEventToPlugins, setting on mobxStore newAgentStatus:',
            //     selectedAgentStatus
            // );

            // TODO: cagri chat-module: not sure about below code
            getPluginStore().setNewAgentStatus(selectedAgentStatus); // make sure the mobXStore knows about the selected status, so that it can compare with the incoming status from the call-plugin after above event
            shouldChangeAgentStatusWithApi = false;
            return shouldChangeAgentStatusWithApi;
        }
    });

    return shouldChangeAgentStatusWithApi;
};
